import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  UserMinus,
  TrendingDown,
  Users,
  RefreshCw,
  Check,
  X,
  Mail,
  Phone,
  Eye,
  Filter,
  Clock,
  User,
} from 'lucide-react'
import { customerAlerts, CustomerAlert, AlertStats } from '@/lib/api'
import { cn } from '@/lib/utils'
import { useToast } from '@/components/ui/Toast'
import CustomerDetailModal from './CustomerDetailModal'

// ============================================================================
// COMPONENTES
// ============================================================================

function StatBadge({
  label,
  value,
  color,
}: {
  label: string
  value: number
  color: string
}) {
  return (
    <div className={cn('px-4 py-2 rounded-lg', color)}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs opacity-80">{label}</div>
    </div>
  )
}

function AlertCard({
  alert,
  onDismiss,
  onAction,
  onViewCustomer,
  isLoading,
}: {
  alert: CustomerAlert
  onDismiss: (id: number) => void
  onAction: (id: number, action: string) => void
  onViewCustomer: (email: string) => void
  isLoading: boolean
}) {
  const [showActions, setShowActions] = useState(false)

  const getAlertIcon = () => {
    switch (alert.alert_type) {
      case 'vip_inactive':
        return UserMinus
      case 'spending_decline':
        return TrendingDown
      case 'churn_risk':
        return AlertTriangle
      default:
        return Users
    }
  }

  const getAlertTypeLabel = () => {
    switch (alert.alert_type) {
      case 'vip_inactive':
        return 'VIP Inactivo'
      case 'spending_decline':
        return 'Caída Facturación'
      case 'churn_risk':
        return 'Riesgo Abandono'
      default:
        return alert.alert_type
    }
  }

  const getSeverityColor = () => {
    switch (alert.severity) {
      case 'high':
        return 'bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-400'
      case 'medium':
        return 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-400'
      case 'low':
        return 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'
      default:
        return 'bg-slate-100 text-slate-800'
    }
  }

  const getBorderColor = () => {
    switch (alert.severity) {
      case 'high':
        return 'border-l-danger-500'
      case 'medium':
        return 'border-l-warning-500'
      case 'low':
        return 'border-l-slate-400'
      default:
        return 'border-l-slate-400'
    }
  }

  const Icon = getAlertIcon()

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div
      className={cn(
        'bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 border-l-4 p-4 transition-all hover:shadow-md',
        getBorderColor()
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div
            className={cn(
              'p-2 rounded-lg',
              alert.severity === 'high'
                ? 'bg-danger-100 dark:bg-danger-900/30'
                : alert.severity === 'medium'
                ? 'bg-warning-100 dark:bg-warning-900/30'
                : 'bg-slate-100 dark:bg-slate-700'
            )}
          >
            <Icon
              className={cn(
                'w-5 h-5',
                alert.severity === 'high'
                  ? 'text-danger-600 dark:text-danger-400'
                  : alert.severity === 'medium'
                  ? 'text-warning-600 dark:text-warning-400'
                  : 'text-slate-600 dark:text-slate-400'
              )}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h4 className="font-semibold text-slate-900 dark:text-white truncate">
                {alert.title}
              </h4>
              <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full', getSeverityColor())}>
                {alert.severity === 'high' ? 'Alta' : alert.severity === 'medium' ? 'Media' : 'Baja'}
              </span>
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400">
                {getAlertTypeLabel()}
              </span>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
              {alert.description}
            </p>
            <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
              <span className="flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {alert.customer_email}
              </span>
              {alert.customer_group && (
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {alert.customer_group}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDate(alert.created_at)}
              </span>
            </div>

            {/* Métricas adicionales */}
            {alert.metrics && (
              <div className="flex gap-4 mt-3 text-xs">
                {alert.metrics.days_inactive && (
                  <span className="text-slate-600 dark:text-slate-400">
                    <strong>{alert.metrics.days_inactive}</strong> días inactivo
                  </span>
                )}
                {alert.metrics.total_spent && (
                  <span className="text-slate-600 dark:text-slate-400">
                    <strong>{alert.metrics.total_spent.toLocaleString('es-ES')}€</strong> gastado
                  </span>
                )}
                {alert.metrics.decline_percent && (
                  <span className="text-danger-600 dark:text-danger-400">
                    <strong>{Math.abs(alert.metrics.decline_percent).toFixed(0)}%</strong> caída
                  </span>
                )}
                {alert.metrics.frequency && (
                  <span className="text-slate-600 dark:text-slate-400">
                    <strong>{alert.metrics.frequency}</strong> pedidos
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Acciones */}
        <div className="flex items-center gap-2 ml-4">
          {!showActions ? (
            <button
              onClick={() => setShowActions(true)}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              title="Ver acciones"
            >
              <Eye className="w-5 h-5" />
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => onViewCustomer(alert.customer_email)}
                className="p-2 text-accent-600 hover:bg-accent-100 dark:hover:bg-accent-900/30 rounded-lg transition-colors"
                title="Ver cliente"
              >
                <User className="w-5 h-5" />
              </button>
              <button
                onClick={() => onAction(alert.id, 'Email enviado')}
                disabled={isLoading}
                className="p-2 text-primary-600 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                title="Enviar email"
              >
                <Mail className="w-5 h-5" />
              </button>
              <button
                onClick={() => onAction(alert.id, 'Llamada realizada')}
                disabled={isLoading}
                className="p-2 text-success-600 hover:bg-success-100 dark:hover:bg-success-900/30 rounded-lg transition-colors"
                title="Marcar llamada"
              >
                <Phone className="w-5 h-5" />
              </button>
              <button
                onClick={() => onDismiss(alert.id)}
                disabled={isLoading}
                className="p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                title="Descartar"
              >
                <X className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowActions(false)}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                <Check className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function CustomerAlertsTab() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<string>('pending')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [severityFilter, setSeverityFilter] = useState<string>('')
  const [selectedCustomerEmail, setSelectedCustomerEmail] = useState<string | null>(null)

  // Queries
  const alertsQuery = useQuery({
    queryKey: ['customer-alerts', statusFilter, typeFilter, severityFilter],
    queryFn: () =>
      customerAlerts.list({
        status: statusFilter || undefined,
        alert_type: typeFilter || undefined,
        severity: severityFilter || undefined,
        limit: 100,
      }),
  })

  const statsQuery = useQuery({
    queryKey: ['customer-alerts-stats'],
    queryFn: () => customerAlerts.getStats(),
  })

  // Mutations
  const detectMutation = useMutation({
    mutationFn: () => customerAlerts.runDetection(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customer-alerts'] })
      queryClient.invalidateQueries({ queryKey: ['customer-alerts-stats'] })
      toast.success(
        'Detección completada',
        `${data.new_alerts} nuevas alertas detectadas de ${data.total_alerts} analizadas`
      )
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const dismissMutation = useMutation({
    mutationFn: (alertId: number) => customerAlerts.dismiss(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-alerts'] })
      queryClient.invalidateQueries({ queryKey: ['customer-alerts-stats'] })
      toast.success('Alerta descartada')
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const actionMutation = useMutation({
    mutationFn: ({ alertId, action }: { alertId: number; action: string }) =>
      customerAlerts.markActioned(alertId, action),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customer-alerts'] })
      queryClient.invalidateQueries({ queryKey: ['customer-alerts-stats'] })
      toast.success('Acción registrada', variables.action)
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const handleDismiss = (alertId: number) => {
    dismissMutation.mutate(alertId)
  }

  const handleAction = (alertId: number, action: string) => {
    actionMutation.mutate({ alertId, action })
  }

  const isLoading = dismissMutation.isPending || actionMutation.isPending

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatBadge
          label="Pendientes"
          value={statsQuery.data?.pending || 0}
          color="bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300"
        />
        <StatBadge
          label="Severidad Alta"
          value={statsQuery.data?.by_severity?.high || 0}
          color="bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-300"
        />
        <StatBadge
          label="VIP Inactivos"
          value={statsQuery.data?.by_type?.vip_inactive || 0}
          color="bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
        />
        <StatBadge
          label="Caída Facturación"
          value={statsQuery.data?.by_type?.spending_decline || 0}
          color="bg-accent-100 text-accent-800 dark:bg-accent-900/30 dark:text-accent-300"
        />
        <StatBadge
          label="Riesgo Abandono"
          value={statsQuery.data?.by_type?.churn_risk || 0}
          color="bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300"
        />
      </div>

      {/* Filtros y Acciones */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input py-1.5 text-sm"
            >
              <option value="">Todos los estados</option>
              <option value="pending">Pendientes</option>
              <option value="reviewed">Revisadas</option>
              <option value="actioned">Accionadas</option>
              <option value="dismissed">Descartadas</option>
            </select>
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="input py-1.5 text-sm"
          >
            <option value="">Todos los tipos</option>
            <option value="vip_inactive">VIP Inactivo</option>
            <option value="spending_decline">Caída Facturación</option>
            <option value="churn_risk">Riesgo Abandono</option>
          </select>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="input py-1.5 text-sm"
          >
            <option value="">Todas las severidades</option>
            <option value="high">Alta</option>
            <option value="medium">Media</option>
            <option value="low">Baja</option>
          </select>
        </div>

        <button
          onClick={() => detectMutation.mutate()}
          disabled={detectMutation.isPending}
          className="btn btn-primary"
        >
          <RefreshCw className={cn('w-4 h-4 mr-2', detectMutation.isPending && 'animate-spin')} />
          {detectMutation.isPending ? 'Detectando...' : 'Detectar Alertas'}
        </button>
      </div>

      {/* Lista de Alertas */}
      <div className="space-y-3">
        {alertsQuery.isLoading ? (
          <div className="text-center py-8 text-slate-500">Cargando alertas...</div>
        ) : alertsQuery.isError ? (
          <div className="text-center py-8 text-danger-600">Error cargando alertas</div>
        ) : alertsQuery.data?.alerts.length === 0 ? (
          <div className="card p-8 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              No hay alertas
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-4">
              {statusFilter === 'pending'
                ? 'No hay alertas pendientes. Ejecuta una detección para buscar nuevas.'
                : 'No se encontraron alertas con los filtros seleccionados.'}
            </p>
            {statusFilter === 'pending' && (
              <button
                onClick={() => detectMutation.mutate()}
                disabled={detectMutation.isPending}
                className="btn btn-primary"
              >
                <RefreshCw className={cn('w-4 h-4 mr-2', detectMutation.isPending && 'animate-spin')} />
                Detectar Alertas
              </button>
            )}
          </div>
        ) : (
          alertsQuery.data?.alerts.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              onDismiss={handleDismiss}
              onAction={handleAction}
              onViewCustomer={setSelectedCustomerEmail}
              isLoading={isLoading}
            />
          ))
        )}
      </div>

      {/* Resumen */}
      {alertsQuery.data && alertsQuery.data.alerts.length > 0 && (
        <div className="text-center text-sm text-slate-500">
          Mostrando {alertsQuery.data.alerts.length} de {alertsQuery.data.total} alertas
        </div>
      )}

      {/* Modal de detalle de cliente */}
      {selectedCustomerEmail && (
        <CustomerDetailModal
          email={selectedCustomerEmail}
          onClose={() => setSelectedCustomerEmail(null)}
        />
      )}
    </div>
  )
}
